import { Box, Slide } from "@mui/material";
import { type UseModalState } from "@src/lib/ui-react";
import { type ReactNode } from "react";

import { useSidePanelGroup } from "./SidePanelGroup";
import {
  SIDE_PANEL_TRANSITION_DURATION_MS,
  SIDE_PANEL_TRANSITION_EASING,
  SidePanelSection,
} from "./SidePanelSection";

/**
 * Shared minimum height for side panel headers (the title row next to the close
 * button). Keeps header heights consistent across panels that can open
 * side-by-side on the schedule page — shift details, chat, worker profile,
 * documents, booked shifts, shift block — so adjacent headers line up
 * regardless of whether they render one or two lines of content.
 *
 * Sized to comfortably fit the tallest content (avatar + two text lines) with
 * compact vertical padding; shorter single-line headers grow to match. Apply
 * with `boxSizing: "border-box"` and vertical centering on the header
 * container, paired with `paddingY: 1`.
 */
export const SIDE_PANEL_HEADER_MIN_HEIGHT = "72px";

export interface SidePanelProps {
  /**
   * The content of the panel
   */
  children: ReactNode;
  /**
   * Width of the panel in pixels
   * @default 300
   */
  width?: number;
  /**
   * Modal state from useModalState hook
   */
  modalState: UseModalState;
  closeOnOutsideClick?: boolean;
  /**
   * CSS position for the standalone panel.
   *
   * `"absolute"` (default) anchors to the nearest positioned ancestor, which
   * is correct for schedule-style pages whose layout container is already
   * sized to the viewport. Use `"fixed"` when the panel's ancestor chain can
   * be taller than the viewport (e.g. a scrolling page), so the panel stays
   * pinned to the screen instead of stretching to the ancestor's height.
   *
   * @default "absolute"
   */
  position?: "absolute" | "fixed";
}

/**
 * Simple slide-in panel from the right side.
 * No modal behavior - content behind is fully interactive.
 *
 * Can be used standalone or within a SidePanelGroup for multi-panel layouts.
 *
 * @example Standalone usage
 * ```tsx
 * const panelState = useModalState();
 * <SidePanel modalState={panelState} width={300}>
 *   <PanelContent />
 * </SidePanel>
 * ```
 *
 * @example Within SidePanelGroup
 * ```tsx
 * const detailsState = useModalState();
 * const logState = useModalState();
 *
 * <SidePanelGroup anchor="right">
 *   <SidePanel modalState={detailsState} width={300}>
 *     <ShiftDetails />
 *   </SidePanel>
 *   <SidePanel modalState={logState} width={300}>
 *     <ShiftLog />
 *   </SidePanel>
 * </SidePanelGroup>
 * ```
 */
export function SidePanel(props: SidePanelProps) {
  const { children, width = 300, modalState, position = "absolute" } = props;
  const groupContext = useSidePanelGroup();
  const isOpen = modalState.modalIsOpen;

  // When inside a SidePanelGroup, render as a section
  if (groupContext) {
    return (
      <SidePanelSection anchor={groupContext.anchor} isOpen={isOpen} width={width}>
        {children}
      </SidePanelSection>
    );
  }

  // Standalone mode. `mountOnEnter`/`unmountOnExit` keep the closed panel out
  // of the layout entirely — parked off-screen it would widen the page's
  // scrollable area (horizontal scrollbar) — while Slide handles the
  // enter/exit transitions.
  return (
    <Slide
      appear
      mountOnEnter
      unmountOnExit
      direction="left"
      easing={SIDE_PANEL_TRANSITION_EASING}
      in={isOpen}
      timeout={SIDE_PANEL_TRANSITION_DURATION_MS}
    >
      <Box
        sx={(theme) => ({
          position,
          top: 0,
          right: 0,
          bottom: 0,
          width,
          backgroundColor: theme.palette.background.surface,
          borderLeft: `${theme.borderWidth?.regular} solid ${theme.palette.border?.base}`,
          boxShadow: theme.shadow?.drawer,
          borderRadius: 0,
          zIndex: theme.zIndex.drawer,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        })}
      >
        {children}
      </Box>
    </Slide>
  );
}

SidePanel.displayName = "SidePanel";
